import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { contactSchema } from '../schemas/contactSchema';
import { 
  sendContactEmail, 
  saveFormToLocalStorage, 
  loadFormFromLocalStorage,
  clearFormFromLocalStorage 
} from '../services/emailService';
import { trackConversion, trackEvent } from '../../../utils/analytics';
import { trackConversionEvent } from '../../../utils/tagManager';

export const useContactForm = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success' | 'error' | null

  const form = useForm({
    resolver: zodResolver(contactSchema),
    defaultValues: {
      companyName: '',
      contactPerson: '',
      email: '',
      phone: '',
      projectType: '',
      budget: '',
      timeline: '',
      description: '',
    },
  });

  // 폼 데이터 자동 저장 (디바운스 적용)
  useEffect(() => {
    const subscription = form.watch((formData) => {
      const timeoutId = setTimeout(() => {
        saveFormToLocalStorage(formData);
      }, 1000);

      return () => clearTimeout(timeoutId);
    });

    return () => subscription.unsubscribe();
  }, [form]);

  // 저장된 폼 데이터 불러오기
  useEffect(() => {
    const savedData = loadFormFromLocalStorage();
    if (savedData) {
      Object.keys(savedData).forEach((key) => {
        if (savedData[key]) {
          form.setValue(key, savedData[key]);
        }
      });
    }
  }, [form]);

  const onSubmit = async (data) => {
    setIsSubmitting(true);
    setSubmitStatus(null);

    // 폼 제출 시작 추적
    trackEvent('Contact', 'form_submit_start', data.projectType);

    try {
      const result = await sendContactEmail(data);
      
      if (result.success) {
        setSubmitStatus('success');
        form.reset();
        clearFormFromLocalStorage();
        
        // 전환 추적
        trackConversion('contact_form', data.budget);
        trackConversionEvent('contact_form_submit', {
          project_type: data.projectType,
          budget: data.budget,
          company: data.companyName
        });
        
        // 성공 이벤트 추적
        trackEvent('Contact', 'form_submit_success', data.projectType);
      } else {
        setSubmitStatus('error');
        trackEvent('Contact', 'form_submit_error', 'email_service_error');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitStatus('error');
      trackEvent('Contact', 'form_submit_error', error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    form,
    isSubmitting,
    submitStatus,
    onSubmit,
    resetStatus: () => setSubmitStatus(null),
  };
};